/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {booleanToString} from '@ion/core/src/utils/BooleanUtils';
import CheckBox from '../../components/checkbox/CheckBox';
import {FormItem} from '../../components/forms/FormItem';
import {toggleThemeStyle} from '../../theme/helpers';
import useThemeContext from '../../theme/useThemeContext';
import {DLSTheme} from '../../types/types';

// defines the Inheritable component prop type definitions ...
export interface InheritableProps {
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  label?: string;
  defaultValue?: boolean;
  disabled?: boolean;
};

// defines the default component styles ...
const useStyles = makeStyles((theme: DLSTheme) => ({
  inherited : {
    marginTop   : toggleThemeStyle(38, 0),
    paddingLeft : 10
  }
}));

// defines the radio options for inheritable field ...
export const InheritableOptions = [{
  label    : booleanToString(true),
  value    : true,
  disabled : false
},
{
  label    : booleanToString(false),
  value    : false,
  disabled : false
}];

/**
 * @Inheritable
 *
 * The Inheritable component provides a generalized DLS component which is typically used
 * within the various Entity Editors for displaying and setting an entities inheritable
 * value.
 *
 * ```typescript
 *
 *    import {Inheritable} from '@ion/dls/src/components/buttons/';
 *
 *    <Inheritable onChange={onCheckBoxChange}
 *      name={loading}
 *      disabled={loading} />
 *
 * ```
 *
 * @param {boolean}  onSave callback which is invoked when the user clicks save.
 * @param {boolean}  loading When true, the loading indicator will be displayed.
 * @param {boolean}  disabled When true, the button will be disabled.
 */
export const Inheritable = ({
  onChange,
  name,
  label = translate('common:editors.labels.inheritable'),
  defaultValue = false,
  disabled = false
}: InheritableProps) => {
  const {isCurrent: isCurrentTheme} = useThemeContext();
  const classes                     = useStyles({isCurrentTheme});
  return (
    <FormItem>
      <div className={classes.inherited}>
        <CheckBox label={`${label ?? translate('common:editors.labels.inheritable')}`}
          name={name}
          defaultChecked={defaultValue}
          onChange={onChange}
          disabled={disabled} />
      </div>
    </FormItem>
  );
};

export default Inheritable;
